import { auth } from '@/lib/supabase'

const RAW_API_BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

function normalizeApiBaseUrl(value) {
  const trimmed = String(value || '').replace(/\/+$/, '')
  if (!trimmed) return ''
  return trimmed.endsWith('/api/v1') ? trimmed : `${trimmed}/api/v1`
}

const API_BASE_URL = normalizeApiBaseUrl(RAW_API_BASE_URL)

async function getBillingSession() {
  const { session, error } = await auth.getSession()

  if (error) {
    throw new Error('We could not confirm your billing session. Please sign in again.')
  }

  if (!session?.access_token) {
    throw new Error('Your session expired. Please sign in again.')
  }

  if (!session.user?.id) {
    throw new Error('We could not identify your account. Please sign in again.')
  }

  return session
}

async function parseBillingResponse(response) {
  const text = await response.text()
  const data = text ? JSON.parse(text) : null

  if (!response.ok) {
    const message = data?.message || data?.error || formatBillingDetail(data?.detail)

    if (response.status === 401) {
      throw new Error('Your billing session expired. Please sign in again.')
    }

    if (response.status === 400) {
      throw new Error(message || 'We could not update billing. Please review your account and try again.')
    }

    if (response.status === 503) {
      throw new Error('Billing is temporarily unavailable. Please try again soon.')
    }

    throw new Error(message || 'Billing is unavailable right now. Please try again.')
  }

  return data
}

function formatBillingDetail(detail) {
  if (!Array.isArray(detail)) return detail

  return detail
    .map((item) => item?.msg)
    .filter(Boolean)
    .join(' ')
}

async function billingRequest(path, options = {}) {
  const session = await getBillingSession()
  const headers = {
    Authorization: `Bearer ${session.access_token}`,
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {})
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers
  })

  return parseBillingResponse(response)
}

export const billingService = {
  async startCheckout({ successUrl, cancelUrl, includeTrial = true }) {
    const session = await getBillingSession()

    return billingRequest('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        user_id: session.user.id,
        success_url: successUrl,
        cancel_url: cancelUrl,
        include_trial: includeTrial
      })
    })
  },

  async cancelSubscription() {
    const session = await getBillingSession()

    return billingRequest('/billing/cancel', {
      method: 'POST',
      body: JSON.stringify({
        user_id: session.user.id
      })
    })
  }
}

export default billingService
